// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { BrandSpecial } from '@iheartjane/data-access';
import type {
  SmartSortProduct,
  VirtualBudtenderProduct,
} from '@iheartjane/dm-sdk';
import { Typography } from '@iheartjane/reefer';
import type {
  AppMode,
  Breadcrumb,
  PriceId,
  ProductThreshold,
} from '@iheartjane/types';
import { useEffect, useRef, useState } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type {
  Brand,
  MenuProduct,
  PendingCartProduct,
  ReservationCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/models';
import type {
  EcommPaths,
  UserLocation,
  MenuProduct as ZMenuProduct,
} from '@jane/shared/types';
import { getMaxCartQuantity } from '@jane/shared/util';

import type {
  AddData,
  DeleteData,
  ProductCardContextProps,
} from '../productCardProvider';
import { ProductCardProvider } from '../productCardProvider';
import { SponsoredTag } from '../sponsoredTag/sponsoredTag';
import { ListViewProductCard } from './listView/listViewProductCard';
import { MenuProductCardContainer } from './menuProductCardContainer';
import { ItemContainer, sortByPriceId } from './utils/productCardHelper';

const findCartProductForMenuProduct = (
  cartProducts: PendingCartProduct[],
  menuProductId: string | number,
  storeId: string | number,
  cartStoreId?: string | number
): ReservationCartProduct[] => {
  const cartProductForMenuProduct = cartProducts.filter(
    (cp) => cp.id === menuProductId && cartStoreId === storeId
  ) as unknown as ReservationCartProduct[];

  return cartProductForMenuProduct;
};

const MenuItemContainer = styled.div<{ disabledState?: boolean }>(
  {
    flex: '0 0 auto',
    position: 'relative',
    width: '100%',
  },
  ({ disabledState }) => {
    return [
      {
        cursor: 'default',
        opacity: disabledState ? 0.5 : 1,
      },
    ];
  }
);

export interface LegacyMenuProductCardProps {
  appliedWeightFilter: PriceId | '';
  appMode: AppMode;
  brand?: Brand;
  /** For rendering the jane gold tag */
  brandSpecials?: BrandSpecial[];
  breadcrumbs?: Breadcrumb[];
  carouselView?: boolean;
  cartIsOpen: boolean;
  cartProducts?: DeepReadonly<PendingCartProduct[]>;
  cartStoreId?: string | number;
  compareOverlay?: React.ReactNode;
  currentSpecial?: StoreSpecial;
  customerId?: number | null;
  /** Makes the component transparent, to indicate a product or row is toggled off */
  disabledState?: boolean;
  /** Disables any click actions (and tracking events) on the card and any sub-components */
  disableInteraction?: boolean;
  disableWeightFilter?: boolean;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  hideActions?: boolean;
  isMenuTopRow?: boolean;
  /**
   * Boolean that determines if all the products in the smart instance
   * are sponsored
   */
  isRowSponsored?: boolean;
  itemWidth?: number | null;
  /** Determined in ProductCardWithTracking so we can track Jane Gold deals */
  janeGoldLabel?: string | null;
  listView: boolean;
  maxWeightOption?: number | null;
  menuRowRules?: {
    product_threshold?: ProductThreshold | null;
  };
  onAddToCart?: (addData: AddData) => void;
  onClick?: (selectedWeight: PriceId | null) => void;
  onCloseCart?: () => void;
  onDeleteFromCart?: (deleteData: DeleteData) => void;
  pdpPath?: EcommPaths['menuProduct'];
  product: MenuProduct | ZMenuProduct;
  productInstance?:
    SmartSortProduct<AlgoliaProduct> | VirtualBudtenderProduct<AlgoliaProduct>;
  searchState?: JaneSearchState<AlgoliaProduct>;
  setProductCardWidth?: (arg: number) => void;
  showOnlyWeights?: string[] | null;
  store: DeepReadonly<Store>;
  userLocation: UserLocation;
  weightFilter?: (
    weight: string,
    product: ZMenuProduct | AlgoliaProduct
  ) => boolean;
}

export const LegacyMenuProductCard = ({
  appMode,
  appliedWeightFilter,
  brand,
  brandSpecials,
  breadcrumbs,
  carouselView,
  cartIsOpen,
  cartProducts,
  cartStoreId,
  compareOverlay,
  currentSpecial,
  customerId,
  disabledState,
  disableInteraction,
  disableWeightFilter,
  fromAllSpecials,
  hideActions,
  itemWidth,
  janeGoldLabel,
  listView,
  onClick,
  onCloseCart,
  onAddToCart,
  onDeleteFromCart,
  pdpPath,
  product,
  searchState,
  showOnlyWeights,
  setProductCardWidth,
  store,
  fromSpecialId,
  maxWeightOption,
  userLocation,
  productInstance,
  isRowSponsored = false,
  isMenuTopRow = false,
  menuRowRules,
  weightFilter,
}: LegacyMenuProductCardProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedWeight, setSelectedWeight] = useState<PriceId>('each');

  const inventoryCount =
    !!product && !!selectedWeight
      ? getMaxCartQuantity(product as MenuProduct, selectedWeight)
      : undefined;

  const showInventoryCount = !!inventoryCount && inventoryCount < 4;

  useEffect(() => {
    setProductCardWidth &&
      containerRef.current &&
      setProductCardWidth(
        itemWidth || containerRef.current.getBoundingClientRect().width
      );
  }, []);

  const cartProduct = sortByPriceId(
    findCartProductForMenuProduct(
      cartProducts as PendingCartProduct[],
      product.id,
      store.id,
      cartStoreId
    ),
    product
  ) as ReservationCartProduct[];

  const vbDescription =
    productInstance && 'reasoning' in productInstance
      ? productInstance.reasoning
      : undefined;

  const productCardContext: Partial<ProductCardContextProps> = {
    virtualBudtenderDescription: vbDescription,
    appMode,
    appliedWeightFilter,
    brand,
    brandSpecials,
    breadcrumbs,
    carouselView,
    cartIsOpen,
    cartProduct,
    compareOverlay,
    currentSpecial,
    customerId,
    disableInteraction,
    disableWeightFilter,
    hideActions,
    janeGoldLabel,
    listView,
    onAddToCart,
    onCloseCart,
    onDeleteFromCart,
    pdpPath,
    product: product,
    menuProduct: product as MenuProduct,
    showOnlyWeights,
    searchState,
    selectedWeight,
    setSelectedWeight,
    store,
    trackListViewClick: onClick
      ? () => {
          onClick(selectedWeight);
        }
      : undefined,
    fromSpecialId,
    fromAllSpecials,
    maxWeightOption,
    userLocation,
    menuRowRules,
    weightFilter,
  };

  /**
   * @todo Remove this when legacy code is deleted since product
   * instance will always be available
   */
  const shouldDisplaySponsoredTag = productInstance
    ? isMenuTopRow || isRowSponsored
      ? false
      : productInstance.isSponsored
    : false;

  if (listView) {
    return (
      <ProductCardProvider value={productCardContext}>
        <ListViewProductCard
          data-product-id={product.id}
          isAd={shouldDisplaySponsoredTag}
        />
      </ProductCardProvider>
    );
  }

  return (
    <ProductCardProvider value={productCardContext}>
      <ItemContainer
        data-testid={`product-card-container-${product.id}`}
        ref={containerRef}
      >
        <MenuItemContainer
          data-testid="product-card"
          data-product-id={product.id}
          disabledState={disabledState}
          onClick={
            onClick
              ? () => {
                  onClick(selectedWeight);
                }
              : undefined
          }
        >
          <MenuProductCardContainer
            shouldDisplaySponsoredTag={shouldDisplaySponsoredTag}
          />
        </MenuItemContainer>
        {showInventoryCount && appMode !== 'bloom' && (
          <Typography textAlign="center" variant="mini" mt={8}>
            Only {inventoryCount} left
          </Typography>
        )}
        <SponsoredTag hideText={!shouldDisplaySponsoredTag} mt={8} />
      </ItemContainer>
    </ProductCardProvider>
  );
};
